// EngineeringAdventures.jsx
// DAS-1898: Engineering showcase page for Quinten Moonen

import React from 'react';
import { Box, Typography, Container, Paper, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import BuildIcon from '@mui/icons-material/Build';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ScienceIcon from '@mui/icons-material/Science';

// Engineering-themed styled components
const EngineeringContainer = styled(Container)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
  padding: theme.spacing(4),
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '16px',
  border: '1px solid rgba(0, 188, 212, 0.3)',
  boxShadow: '0 0 30px rgba(0, 188, 212, 0.1)',
}));

const ProjectCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(4),
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid rgba(0, 188, 212, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateX(8px)',
    boxShadow: '0 8px 32px rgba(0, 188, 212, 0.2)',
    border: '1px solid rgba(0, 188, 212, 0.5)',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const ProjectImage = styled('img')(({ theme }) => ({
  width: '400px',
  height: '300px',
  objectFit: 'cover',
  flexShrink: 0,
  borderRight: '2px solid rgba(0, 188, 212, 0.3)',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: '250px',
    borderRight: 'none',
    borderBottom: '2px solid rgba(0, 188, 212, 0.3)',
  },
}));

const ProjectContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
}));

const ProjectTitle = styled(Typography)(({ theme }) => ({
  color: '#00bcd4',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontFamily: '"Roboto Mono", monospace',
}));

const ProjectDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.85)',
  lineHeight: 1.8,
  fontSize: '1rem',
}));

const TechBadge = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: '4px 12px',
  margin: '4px',
  background: 'rgba(0, 188, 212, 0.15)',
  border: '1px solid rgba(0, 188, 212, 0.4)',
  borderRadius: '20px',
  color: '#00bcd4',
  fontSize: '0.75rem',
  fontFamily: '"Roboto Mono", monospace',
}));

const CircuitLine = styled(Divider)(({ theme }) => ({
  borderColor: 'rgba(0, 188, 212, 0.3)',
  margin: theme.spacing(2, 0),
  '&::before, &::after': {
    borderColor: 'rgba(0, 188, 212, 0.3)',
  },
}));

// Project data
const projects = [
  {
    id: 1,
    image: '/engineering_adventures/1Masterstudy.png',
    title: 'Remote Digital Holography',
    subtitle: 'Master\'s Thesis Project',
    description: 'Pioneering research in next-generation optical networks through Remote Digital Holography with Optical Injection Locking. This groundbreaking work achieved stable 23.5 GHz locking range, enabling robust performance for Free-Space Optical (FSO) communication systems. Successfully demonstrated over a 4.6 km FSO link in Eindhoven with 86-98% stability.',
    technologies: ['Optical Engineering', 'Fiber Optics', 'Laser Systems', 'Signal Processing', 'FSO Communications'],
    icon: <ScienceIcon />,
  },
  {
    id: 2,
    image: '/engineering_adventures/2longbike.jpeg',
    title: 'MoonHoon Cargo Bike',
    subtitle: 'Custom Frame Design & Fabrication',
    description: 'A custom-designed and built cargo bike featuring a unique extended frame geometry. This project involved complete CAD design, steel tube selection and cutting, TIG welding, powder coating, and integration of a multi-speed drivetrain with hydraulic disc brakes. The result is a practical urban cargo solution with distinctive purple aesthetics.',
    technologies: ['CAD Design', 'TIG Welding', 'Steel Fabrication', 'Mechanical Engineering', 'Powder Coating'],
    icon: <BuildIcon />,
  },
  {
    id: 3,
    image: '/engineering_adventures/3drone.JPG',
    title: 'Custom FPV Racing Drone',
    subtitle: 'Aerial Photography Platform',
    description: 'A purpose-built FPV drone designed for high-altitude mountain photography. Features a custom carbon fiber frame, high-performance brushless motors, integrated GoPro Hero mount, and optimized flight controller tuning. Tested extensively in alpine conditions for stability and camera performance.',
    technologies: ['Aerodynamics', 'Electronics', 'Flight Controllers', 'Carbon Fiber', 'RC Systems'],
    icon: <PrecisionManufacturingIcon />,
  },
  {
    id: 4,
    image: '/engineering_adventures/4longboard.JPG',
    title: 'Handcrafted Longboard',
    subtitle: 'Wood Engineering & Design',
    description: 'A completely handcrafted longboard built from layered hardwood with custom flex characteristics. This project combined woodworking precision with skateboard engineering principles, featuring hand-shaped deck profile, custom wheel selection, and precision truck mounting for optimal carving performance.',
    technologies: ['Woodworking', 'Material Science', 'CNC Routing', 'Composite Layering', 'Product Design'],
    icon: <EngineeringIcon />,
  },
];

const EngineeringAdventures = () => {
  return (
    <EngineeringContainer maxWidth={false}>
      <Container maxWidth="lg">
        {/* Header */}
        <HeaderSection>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <EngineeringIcon sx={{ fontSize: 48, color: '#00bcd4' }} />
            <PrecisionManufacturingIcon sx={{ fontSize: 40, color: 'rgba(0, 188, 212, 0.7)' }} />
            <BuildIcon sx={{ fontSize: 36, color: 'rgba(0, 188, 212, 0.5)' }} />
          </Box>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 700, 
              mb: 2,
              fontFamily: '"Roboto Mono", monospace',
              textShadow: '0 0 20px rgba(0, 188, 212, 0.5)',
            }}
          >
            Engineering Adventures
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#00bcd4', 
              fontWeight: 500,
              letterSpacing: '0.1em',
            }}
          >
            of Quinten Moonen Ir.
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)', 
              mt: 1,
              fontStyle: 'italic',
            }}
          >
            Master in Electrical Engineering
          </Typography>
          <CircuitLine />
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.5)', 
              fontFamily: '"Roboto Mono", monospace',
              fontSize: '0.85rem',
            }}
          >
            // A showcase of hands-on engineering projects combining theory with practical innovation
          </Typography>
        </HeaderSection>

        {/* Project Cards */}
        {projects.map((project) => (
          <ProjectCard key={project.id} elevation={0}>
            <ProjectImage 
              src={project.image} 
              alt={project.title}
              loading="lazy"
            />
            <ProjectContent>
              <ProjectTitle variant="h5">
                {project.icon}
                {project.title}
              </ProjectTitle>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: 'rgba(0, 188, 212, 0.7)', 
                  mb: 2,
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: '0.85rem',
                }}
              >
                {'>'} {project.subtitle}
              </Typography>
              <ProjectDescription variant="body1">
                {project.description}
              </ProjectDescription>
              <Box sx={{ mt: 3 }}>
                {project.technologies.map((tech, index) => (
                  <TechBadge key={index}>{tech}</TechBadge>
                ))}
              </Box>
            </ProjectContent>
          </ProjectCard>
        ))}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, py: 3 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: '"Roboto Mono", monospace',
            }}
          >
            © {new Date().getFullYear()} Quinten Moonen | Built with passion for engineering
          </Typography>
        </Box>
      </Container>
    </EngineeringContainer>
  );
};

export default EngineeringAdventures;
